import Image from 'next/image'
import React, { type PropsWithChildren, useMemo } from 'react'
import { useTheme } from 'styled-components'
import key from 'weak-key'

import Col from '@components/atoms/Grid/Col'
import Row from '@components/atoms/Grid/Row'

import { type PropsWithClassName } from '@helper/PropsWithClassName'

import { Coupon, Studio } from '@definitions/types/symfonyTypesd'

import { CouponItem } from './CouponItem'
import { CouponItemContainer } from './CouponItemContainer'
import { CouponContent, CouponList } from './Coupons.styles'

interface Props extends PropsWithClassName {
  coupons: Coupon[]
  studio: Studio
}

const createCouponImageUrl = (coupon: Coupon): string => {
  const path = coupon.frontendImage?.publicPath
  if (!path) {
    return ''
  }
  return `${process.env.symfonyUrl}/${path}`
}

const couponType = 'cosmetics'

const Coupons: React.FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
): React.ReactElement => {
  const { coupons, studio } = props
  const theme: any = useTheme()

  const couponsFiltered = useMemo(() => {
    const newCoupons = coupons.filter((coupon) => {
      return (
        (coupon.affiliations?.edges ?? []).filter(
          (e) => e?.node?.name.toLowerCase() === theme.key
        ).length > 0
      )
    })

    newCoupons.sort((couponA, couponB) => couponA.sort - couponB.sort)

    return newCoupons
  }, [coupons, theme.key])

  return (
    <CouponList className={props.className} data-testid={'coupons-root'}>
      <Row>
        {couponsFiltered.map((coupon) => {
          const isNeutral = 'cosmetics' === couponType
          const id = key(coupon)
          return (
            <Col key={'coupon' + coupon.id + id} xs={12} sm={12} md={6} lg={6}>
              <CouponItemContainer type={couponType}>
                <CouponItem formId={id} coupon={coupon} studio={studio}>
                  <CouponContent>
                    {coupon.frontendImage?.publicPath && (
                      <Image
                        src={createCouponImageUrl(coupon)}
                        alt={coupon.title}
                        width={608}
                        height={217}
                      />
                    )}
                  </CouponContent>
                </CouponItem>
              </CouponItemContainer>
            </Col>
          )
        })}
      </Row>
    </CouponList>
  )
}

export { Coupons }
